import * as React from 'react';
import {
  Connection as StardogConnection,
  query as stardogQuery,
} from 'stardog';
import { getStardogConnection } from 'vet-bones/utils';
import { refetchConnectionToken } from 'vet-bones/utils/portal';

import { catalogDatabaseSelector } from 'src/selectors/catalogDatabase';
import { connectionSelector } from 'src/selectors/connection';
import { isStardogFeatureSupportedSelector } from 'src/selectors/isStardogFeatureSupported';
import { CatalogTableColumnsBindingResult } from 'src/templates/queries/catalogPrimaryKeyTableColumns';
import {
  buildCatalogTableColumnsQuery,
  parseCatalogColumnResults,
} from 'src/templates/queries/catalogTableColumns';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type CatalogTableColumns = {
  /** Column names keyed by `getDataSourceTableKey` (catalog.schema.name). */
  columnsByTableKey: Record<string, string[]>;
  error: boolean;
  isLoading: boolean;
  /** Fetch (or re-fetch) the catalog columns. */
  refetch: () => void;
};

/**
 * Fetches every column name for a data source's tables from the Catalog DB.
 * Used when resource previews are disabled (VET-6585): the expensive
 * `SELECT * ... LIMIT` preview query is skipped, so column headers come from
 * the catalog instead.
 *
 * The columns are copied onto the virtual graph's `fields` by the caller and
 * persisted there (just like a normal virtual graph), so this hook does not
 * cache. It fetches only when `enabled` — e.g. a selected table has no fields
 * yet — and via `refetch()` (the preview refresh button). Re-opening a dialog
 * reuses the fields already saved on the virtual graph, so no query runs. The
 * fetch is gated on the connection supporting the catalog; otherwise it returns
 * an empty map and callers fall back to the empty-state messaging.
 */
export const useCatalogTableColumns = ({
  dataSourceId,
  enabled,
}: {
  dataSourceId?: string;
  enabled: boolean;
}): CatalogTableColumns => {
  const connection = useReduxSelector(connectionSelector);
  const catalogDatabaseId = useReduxSelector(catalogDatabaseSelector);
  const isCatalogSupported = useReduxSelector(
    isStardogFeatureSupportedSelector('isCatalogSupported', connection)
  );
  const isMounted = useIsMounted();

  const [columnsByTableKey, setColumnsByTableKey] = React.useState<
    Record<string, string[]>
  >({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(false);

  const canFetch = Boolean(isCatalogSupported && dataSourceId && connection);

  const refetch = React.useCallback(() => {
    if (!canFetch || !dataSourceId || !connection) {
      return;
    }

    setIsLoading(true);
    setError(false);

    const query = buildCatalogTableColumnsQuery([dataSourceId]);
    const wrappedRequest = (stardogConn: StardogConnection) =>
      stardogQuery.execute(stardogConn, catalogDatabaseId, query);

    wrappedRequest(getStardogConnection(connection, { agent: 'Designer' }))
      .then((response) =>
        response && response.status === 401
          ? refetchConnectionToken(connection.index).then((token) =>
              token
                ? wrappedRequest(
                    getStardogConnection(
                      { ...connection, token },
                      { agent: 'Designer' }
                    )
                  )
                : response
            )
          : response
      )
      .then((response) => {
        if (!isMounted.current) {
          return;
        }
        if (!response.ok) {
          setError(true);
          setColumnsByTableKey({});
        } else {
          const bindings: CatalogTableColumnsBindingResult[] =
            response.body?.results?.bindings || [];
          setColumnsByTableKey(parseCatalogColumnResults(bindings));
        }
        setIsLoading(false);
      })
      .catch(() => {
        if (!isMounted.current) {
          return;
        }
        setError(true);
        setColumnsByTableKey({});
        setIsLoading(false);
      });
  }, [canFetch, catalogDatabaseId, connection, dataSourceId, isMounted]);

  React.useEffect(() => {
    if (enabled) {
      refetch();
    }
  }, [enabled, refetch]);

  return { columnsByTableKey, error, isLoading, refetch };
};
